import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import { AuthProvider } from "@/contexts/AuthContext";
import Login from "@/pages/Login";
import Dashboard from "@/pages/Dashboard";
import Jobs from "@/pages/Jobs";
import JobDetail from "@/pages/JobDetail";
import JobPrint from "@/pages/JobPrint";
import CalendarPage from "@/pages/CalendarPage";
import Employees from "@/pages/Employees";
import MyJobs from "@/pages/MyJobs";
import Vehicles from "@/pages/Vehicles";
import VehicleHistory from "@/pages/VehicleHistory";
import Expenses from "@/pages/Expenses";
import Leaves from "@/pages/Leaves";
import Timesheet from "@/pages/Timesheet";
import Anomalies from "@/pages/Anomalies";
import Layout from "@/components/app/Layout";
import Settings from "@/pages/Settings";
import PersonalArea from "@/pages/PersonalArea";
import EmployeeRequests from "@/pages/EmployeeRequests";
import Privacy from "@/pages/Privacy";
import Documents from "@/pages/Documents";
import Training from "@/pages/Training";
import CourseView from "@/pages/CourseView";
import CourseEditor from "@/pages/CourseEditor";
import AuthCallback from "@/components/app/AuthCallback";
import Archive from "@/pages/Archive";
import Payroll from "@/pages/Payroll";
import Quotes from "@/pages/Quotes";
import QuoteDetail from "@/pages/QuoteDetail";
import PriceList from "@/pages/PriceList";
import Support from "@/pages/Support";

function Protected({ children, adminOnly = false, ownerOnly = false }) {
  const { user, loading } = useAuth();
  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-slate-500 text-sm">Caricamento…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== "admin") return <Navigate to="/miei-lavori" replace />;
  if (ownerOnly && user.office) return <Navigate to="/dashboard" replace />;
  return children;
}

function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (!user) return <Navigate to="/login" replace />;
  return <Navigate to={user.role === "admin" ? "/dashboard" : "/miei-lavori"} replace />;
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/stampa/:id" element={<Protected><JobPrint /></Protected>} />
          <Route element={<Protected><Layout /></Protected>}>
            <Route path="/dashboard" element={<Protected adminOnly><Dashboard /></Protected>} />
            <Route path="/lavori" element={<Protected adminOnly><Jobs /></Protected>} />
            <Route path="/lavori/:id" element={<Protected><JobDetail /></Protected>} />
            <Route path="/calendario" element={<Protected adminOnly><CalendarPage /></Protected>} />
            <Route path="/dipendenti" element={<Protected adminOnly><Employees /></Protected>} />
            <Route path="/miei-lavori" element={<Protected><MyJobs /></Protected>} />
            <Route path="/mezzi" element={<Protected adminOnly><Vehicles /></Protected>} />
            <Route path="/storico-mezzi" element={<Protected adminOnly><VehicleHistory /></Protected>} />
            <Route path="/nota-spese" element={<Protected><Expenses /></Protected>} />
            <Route path="/ferie" element={<Protected><Leaves /></Protected>} />
            <Route path="/presenze" element={<Protected><Timesheet /></Protected>} />
            <Route path="/anomalie" element={<Protected adminOnly><Anomalies /></Protected>} />
            <Route path="/impostazioni" element={<Protected adminOnly ownerOnly><Settings /></Protected>} />
            <Route path="/richieste" element={<Protected adminOnly><EmployeeRequests /></Protected>} />
            <Route path="/area-personale" element={<Protected><PersonalArea /></Protected>} />
            <Route path="/documenti" element={<Protected adminOnly><Documents /></Protected>} />
            <Route path="/archivio" element={<Protected><Archive /></Protected>} />
            <Route path="/paghe" element={<Protected adminOnly ownerOnly><Payroll /></Protected>} />
            <Route path="/preventivi" element={<Protected adminOnly><Quotes /></Protected>} />
            <Route path="/preventivi/:id" element={<Protected adminOnly><QuoteDetail /></Protected>} />
            <Route path="/listino" element={<Protected adminOnly><PriceList /></Protected>} />
            <Route path="/assistenza" element={<Protected><Support /></Protected>} />
            <Route path="/formazione" element={<Protected><Training /></Protected>} />
            <Route path="/formazione/:id" element={<Protected><CourseView /></Protected>} />
            <Route path="/formazione/:id/modifica" element={<Protected adminOnly><CourseEditor /></Protected>} />
          </Route>
          <Route path="*" element={<RootRedirect />} />
        </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Toaster richColors position="top-right" />
        <AppRouter />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
